"use client"

import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button } from "../../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar"
import { Separator } from "../../../components/ui/separator"
import Navbar from "../../../components/Navbar"
import Footer from "../../../components/Footer"
import { useLanguage } from "../../../contexts/LanguageContext"
import { 
  Star, 
  Users, 
  Calendar,
  Globe,
  Mail,
  ExternalLink,
  ArrowLeft,
  Check,
  X,
  Download,
  Play,
  BookOpen,
  MessageCircle,
  Heart,
  Share2
} from "lucide-react"

// Mock data for tool details
const mockToolDetails = {
  1: {
    id: 1,
    name: "智能客服助手",
    description: "24/7智能客服系统，自动处理客户咨询，提升客户满意度和服务效率",
    longDescription: `智能客服助手是一款革命性的AI驱动客户服务解决方案，专为企业提供全天候、高质量的客户支持服务。

核心功能：
• 24/7全天候自动客服
• 多语言智能对话支持
• 智能问题分类和路由
• 实时情感分析和客户满意度监控
• 知识库自动学习和更新
• 与CRM系统无缝集成
• 多渠道支持（网页、APP、微信等）
• 人工客服智能转接

通过先进的自然语言处理技术，智能客服助手能够理解客户意图，提供准确的回答，并在需要时无缝转接人工客服，大幅提升客户服务效率和满意度。`,
    category: "客户服务",
    price: 299.99,
    priceType: "monthly",
    rating: 4.8,
    reviewsCount: 1247,
    developer: {
      name: "ServiceAI Pro",
      description: "专注于企业级AI客服解决方案的领先技术公司",
      website: "https://serviceaipro.com",
      email: "contact@serviceaipro.com",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
    },
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=400&fit=crop",
    tags: ["客服自动化", "多语言支持", "智能分流", "情感分析", "CRM集成"],
    featured: true,
    version: "3.2.0",
    lastUpdated: "2024-01-15",
    requirements: {
      os: ["Windows Server 2016+", "Linux Ubuntu 18.04+", "macOS 10.15+"],
      memory: "8GB RAM minimum",
      storage: "50GB free space",
      internet: "必需，用于AI模型更新和云服务"
    },
    pricing: {
      plans: [
        {
          name: "基础版",
          price: 0,
          features: ["基础客服机器人", "100次对话/月", "邮件支持"],
          popular: false
        },
        {
          name: "专业版",
          price: 299.99,
          period: "monthly",
          features: [
            "无限对话次数",
            "多语言支持",
            "智能分流",
            "7×24技术支持",
            "自定义知识库",
            "团队协作功能"
          ],
          popular: true
        },
        {
          name: "企业版",
          price: 999.99,
          period: "monthly",
          features: [
            "专业版全部功能",
            "定制AI训练",
            "专属客户经理",
            "高级分析报告",
            "SSO单点登录",
            "API集成支持"
          ],
          popular: false
        }
      ]
    },
    reviews: [
      {
        id: 1,
        user: "张经理",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face",
        rating: 5,
        date: "2024-01-10",
        title: "客服效率提升显著",
        content: "这款智能客服系统彻底改变了我们的客户服务流程。AI回答准确率高，客户满意度明显提升，人工客服工作量减少了60%。强烈推荐！"
      },
      {
        id: 2,
        user: "李总监",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
        rating: 4,
        date: "2024-01-08",
        title: "功能强大，有待优化",
        content: "整体功能很强大，多语言支持做得很好。智能分流功能特别实用。希望后续能增加更多行业模板和更精细的权限管理。"
      },
      {
        id: 3,
        user: "王主管",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face",
        rating: 5,
        date: "2024-01-05",
        title: "用过最好的AI客服系统",
        content: "我们试过很多AI客服产品，这个是最优秀的。对话自然流畅，知识库学习能力强，与现有系统集成也很方便。"
      }
    ],
    usageInstructions: {
      installation: [
        "从官网下载安装包",
        "运行安装程序并按照向导完成安装",
        "配置数据库连接",
        "设置管理员账户和权限"
      ],
      setup: [
        "登录管理后台",
        "配置客服机器人参数",
        "导入企业知识库",
        "设置对话流程和转接规则",
        "测试客服功能"
      ],
      usage: [
        "客户通过网页或APP发起咨询",
        "AI自动识别问题类型并回答",
        "复杂问题自动转接人工客服",
        "实时监控对话质量和客户满意度",
        "定期更新知识库和优化回答"
      ]
    }
  },
  13: {
    id: 13,
    name: "智能自动营销触达",
    description: "全渠道智能营销自动化平台，精准客户触达，提升转化率和营销ROI",
    longDescription: `智能自动营销触达是一款革命性的全渠道营销自动化解决方案，专为企业提供精准、高效的客户触达和转化优化服务。

核心功能：
• 全渠道营销自动化（邮件、短信、微信、APP推送等）
• 智能客户分群和个性化内容推荐
• 实时营销效果分析和A/B测试
• 客户行为预测和生命周期管理
• 营销活动自动触发和优化
• 与CRM、ERP系统无缝集成
• 多渠道数据统一管理和分析
• 智能营销策略推荐

通过先进的机器学习和数据分析技术，智能自动营销触达能够精准识别目标客户，自动执行营销策略，大幅提升营销效率和转化率。`,
    category: "营销自动化",
    price: 199.99,
    priceType: "monthly",
    rating: 4.7,
    reviewsCount: 892,
    developer: {
      name: "MarketingAI",
      description: "专注于企业级智能营销解决方案的领先技术公司",
      website: "https://marketingai.com",
      email: "contact@marketingai.com",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
    },
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop",
    tags: ["营销自动化", "客户触达", "转化优化", "个性化推荐", "数据分析"],
    featured: true,
    version: "2.8.0",
    lastUpdated: "2024-01-20",
    requirements: {
      os: ["Windows Server 2016+", "Linux Ubuntu 18.04+", "macOS 10.15+"],
      memory: "16GB RAM minimum",
      storage: "100GB free space",
      internet: "必需，用于实时数据同步和云服务"
    },
    pricing: {
      plans: [
        {
          name: "基础版",
          price: 0,
          features: ["基础营销自动化", "1000次触达/月", "邮件支持"],
          popular: false
        },
        {
          name: "专业版",
          price: 199.99,
          period: "monthly",
          features: [
            "无限触达次数",
            "全渠道营销",
            "智能客户分群",
            "7×24技术支持",
            "高级分析报告",
            "A/B测试功能"
          ],
          popular: true
        },
        {
          name: "企业版",
          price: 799.99,
          period: "monthly",
          features: [
            "专业版全部功能",
            "定制AI模型",
            "专属客户经理",
            "实时数据同步",
            "SSO单点登录",
            "API集成支持"
          ],
          popular: false
        }
      ]
    },
    reviews: [
      {
        id: 1,
        user: "陈总监",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face",
        rating: 5,
        date: "2024-01-18",
        title: "营销效率提升显著",
        content: "这款营销自动化平台彻底改变了我们的营销策略。客户触达精准度大幅提升，转化率增长了40%，营销ROI明显改善。强烈推荐！"
      },
      {
        id: 2,
        user: "刘经理",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
        rating: 4,
        date: "2024-01-15",
        title: "功能强大，操作简单",
        content: "全渠道营销功能很强大，客户分群和个性化推荐做得很好。A/B测试功能特别实用。希望后续能增加更多行业模板。"
      },
      {
        id: 3,
        user: "张主管",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face",
        rating: 5,
        date: "2024-01-12",
        title: "用过最好的营销自动化平台",
        content: "我们试过很多营销自动化产品，这个是最优秀的。自动化程度高，数据分析深入，与现有系统集成也很方便。"
      }
    ],
    usageInstructions: {
      installation: [
        "从官网下载安装包",
        "运行安装程序并按照向导完成安装",
        "配置数据库和消息队列",
        "设置管理员账户和权限"
      ],
      setup: [
        "登录管理后台",
        "配置营销渠道参数",
        "导入客户数据和分群规则",
        "设置营销活动模板",
        "测试营销流程"
      ],
      usage: [
        "创建营销活动和客户分群",
        "设计个性化营销内容",
        "设置触发条件和自动化流程",
        "实时监控营销效果和转化率",
        "根据数据分析优化营销策略"
      ]
    }
  }
}

export default function ToolDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useLanguage()
  const [tool, setTool] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      const toolId = parseInt(id || '1')
      const toolData = mockToolDetails[toolId as keyof typeof mockToolDetails]
      setTool(toolData || null)
      setLoading(false)
    }, 500)
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!tool) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('marketplace.toolNotFound')}</h2>
            <Button onClick={() => navigate('/marketplace')}>
              {t('marketplace.backToMarketplace')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <section className="pt-20 pb-8 px-4 bg-white border-b">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/marketplace')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('marketplace.backToMarketplace')}
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Tool Image */}
            <div className="lg:col-span-1">
              <img
                src={tool.image}
                alt={tool.name}
                className="w-full h-64 lg:h-80 object-cover rounded-lg"
              />
            </div>

            {/* Tool Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{tool.name}</h1>
                  <p className="text-lg text-gray-600 mb-4">{tool.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600">${tool.price}</div>
                  <div className="text-gray-500">/{tool.priceType}</div>
                </div>
              </div>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 text-yellow-500 fill-current" />
                  <span className="font-medium">{tool.rating}</span>
                  <span className="text-gray-500">({tool.reviews} {t('marketplace.reviews')})</span>
                </div>
                <Badge variant="secondary">{tool.category}</Badge>
                {tool.featured && <Badge className="bg-yellow-500">{t('marketplace.featured')}</Badge>}
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {tool.tags.map((tag: string, index: number) => (
                  <Badge key={index} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>

              <div className="flex gap-4">
                <Button size="lg" className="flex-1">
                  <Download className="mr-2 h-4 w-4" />
                  {t('marketplace.getStarted')}
                </Button>
                <Button size="lg" variant="outline">
                  <Heart className="mr-2 h-4 w-4" />
                  {t('marketplace.favorite')}
                </Button>
                <Button size="lg" variant="outline">
                  <Share2 className="mr-2 h-4 w-4" />
                  {t('marketplace.share')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Tabs */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">{t('marketplace.tabs.overview')}</TabsTrigger>
              <TabsTrigger value="pricing">{t('marketplace.tabs.pricing')}</TabsTrigger>
              <TabsTrigger value="instructions">{t('marketplace.tabs.instructions')}</TabsTrigger>
              <TabsTrigger value="developer">{t('marketplace.tabs.developer')}</TabsTrigger>
              <TabsTrigger value="reviews">{t('marketplace.tabs.reviews')}</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('marketplace.overview')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed">{tool.longDescription}</p>
                  </div>
                  
                  <Separator className="my-6" />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold mb-3">{t('marketplace.requirements')}</h3>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li><strong>{t('marketplace.os')}:</strong> {tool.requirements.os.join(', ')}</li>
                        <li><strong>{t('marketplace.memory')}:</strong> {tool.requirements.memory}</li>
                        <li><strong>{t('marketplace.storage')}:</strong> {tool.requirements.storage}</li>
                        <li><strong>{t('marketplace.internet')}:</strong> {tool.requirements.internet}</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">{t('marketplace.details')}</h3>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li><strong>{t('marketplace.version')}:</strong> {tool.version}</li>
                        <li><strong>{t('marketplace.lastUpdated')}:</strong> {tool.lastUpdated}</li>
                        <li><strong>{t('marketplace.category')}:</strong> {tool.category}</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pricing Tab */}
            <TabsContent value="pricing" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {tool.pricing.plans.map((plan: any, index: number) => (
                  <Card key={index} className={`relative ${plan.popular ? 'ring-2 ring-blue-500' : ''}`}>
                    {plan.popular && (
                      <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500">
                        {t('marketplace.mostPopular')}
                      </Badge>
                    )}
                    <CardHeader>
                      <CardTitle className="text-center">{plan.name}</CardTitle>
                      <div className="text-center">
                        <span className="text-3xl font-bold">${plan.price}</span>
                        {plan.period && <span className="text-gray-500">/{plan.period}</span>}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {plan.features.map((feature: string, featureIndex: number) => (
                          <li key={featureIndex} className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-green-500" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button className="w-full mt-6" variant={plan.popular ? "default" : "outline"}>
                        {t('marketplace.choosePlan')}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Instructions Tab */}
            <TabsContent value="instructions" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Download className="h-5 w-5" />
                      {t('marketplace.installation')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-2 text-sm">
                      {tool.usageInstructions.installation.map((step: string, index: number) => (
                        <li key={index} className="flex gap-2">
                          <span className="font-medium text-blue-600">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      {t('marketplace.setup')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-2 text-sm">
                      {tool.usageInstructions.setup.map((step: string, index: number) => (
                        <li key={index} className="flex gap-2">
                          <span className="font-medium text-blue-600">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Play className="h-5 w-5" />
                      {t('marketplace.usage')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-2 text-sm">
                      {tool.usageInstructions.usage.map((step: string, index: number) => (
                        <li key={index} className="flex gap-2">
                          <span className="font-medium text-blue-600">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Developer Tab */}
            <TabsContent value="developer" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    {t('marketplace.developer')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={tool.developer.avatar} />
                      <AvatarFallback>{tool.developer.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{tool.developer.name}</h3>
                      <p className="text-gray-600 mb-4">{tool.developer.description}</p>
                      <div className="flex gap-4">
                        <Button variant="outline" size="sm">
                          <Globe className="mr-2 h-4 w-4" />
                          {t('marketplace.visitWebsite')}
                        </Button>
                        <Button variant="outline" size="sm">
                          <Mail className="mr-2 h-4 w-4" />
                          {t('marketplace.contact')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews" className="mt-6">
              <div className="space-y-6">
                {tool.reviews.map((review: any) => (
                  <Card key={review.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <Avatar>
                          <AvatarImage src={review.avatar} />
                          <AvatarFallback>{review.user.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold">{review.user}</h4>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${
                                    i < review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <h5 className="font-medium mb-2">{review.title}</h5>
                          <p className="text-gray-600 text-sm mb-2">{review.content}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>{review.date}</span>
                            <Button variant="ghost" size="sm">
                              <MessageCircle className="mr-1 h-3 w-3" />
                              {t('marketplace.reply')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  )
} 